import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { ActionSheetController, IonTextarea, ModalController, Platform } from '@ionic/angular';
import { MHProduct } from 'src/app/interfaces/api/product';
import { ControllerService } from 'src/app/services/controller.service';
import { ModalOpenFullimageComponent } from '../modal-open-fullimage/modal-open-fullimage.component';
import { Filesystem } from '@capacitor/filesystem';
import { CaptureImageOptions, MediaCapture, MediaFile } from '@awesome-cordova-plugins/media-capture/ngx';
import { CapacitorVideoPlayer } from 'capacitor-video-player';

import { FileOpener } from '@capawesome-team/capacitor-file-opener';
import { FilePicker, PickedFile } from '@capawesome/capacitor-file-picker';
import { Capacitor } from '@capacitor/core';
import { ModalCustomAlertComponent } from '../modal-custom-alert/modal-custom-alert.component';

export type ProductRatingCardPayload = {
  productId: number;
  review_star?: number;
  review_note?: string;
  review_images?: string[];
  review_video?: string;
};

type VideoTakenWith = 'recording' | 'file_picker';

@Component({
  selector: 'app-product-rating-card',
  templateUrl: './product-rating-card.component.html',
  styleUrls: ['./product-rating-card.component.scss'],
})
export class ProductRatingCardComponent implements OnInit {
  @Input() product: MHProduct;
  @Input() isLoading: boolean;
  @Output() changeRating = new EventEmitter();
  isNative: boolean;
  isMobile: boolean;
  selectedRating: number;
  review_note: string;

  video: MediaFile | File | PickedFile;
  videoPath: string;
  videoBase64: string;
  videoTakenWith: VideoTakenWith;

  uploadedImages: { id: string; base64: string; previewBase64: string }[] = [];

  constructor(
    private controllerService: ControllerService,
    private modalController: ModalController,
    private mediaCapture: MediaCapture,
    private actionSheetController: ActionSheetController,
    private platform: Platform,
  ) {}

  async ngOnInit() {
    const platform = Capacitor.getPlatform();
    this.isNative = platform === 'android' || platform === 'ios';
    this.isMobile = window.innerWidth <= 576;
  }

  changeRatingHandler() {
    const payload: ProductRatingCardPayload = {
      productId: this.product.id,
      review_star: this.selectedRating,
      review_note: this.review_note,
      review_images: this.uploadedImages.map(image => image.base64),
      review_video: this.videoBase64,
    };

    this.changeRating.emit(payload);
  }

  async onUploadImage() {
    if (this.uploadedImages.length >= 5) {
      this.controllerService.presentToast({
        message: 'Maksimum upload gambar adalah 5',
        color: 'warning',
        duration: 3000,
      });
      return;
    }

    const image = await Camera.getPhoto({
      quality: 40,
      allowEditing: false,
      resultType: CameraResultType.Base64,
      source: this.isNative ? CameraSource.Prompt : CameraSource.Photos,
    });

    if (image.base64String.length > 2_000_000) {
      this.controllerService.presentToast({
        message: 'Ukuran file tidak boleh lebih dari 2MB',
        color: 'danger',
        duration: 3000,
      });
      return;
    }

    const base64 = image.base64String;
    // console.log(base64);
    const previewBase64 = `data:image/jpeg;base64,${base64}`;

    this.uploadedImages.push({
      id: `img-${this.uploadedImages.length.toString()}`,
      base64,
      previewBase64,
    });

    this.changeRatingHandler();
  }

  async onRecordVideo() {
    let capturedVideo: MediaFile;

    try {
      const options: CaptureImageOptions = { limit: 1 };
      const data = await this.mediaCapture.captureVideo(options);
      capturedVideo = data[0];
    } catch (e) {
      console.log(e);
      return;
    }

    if (capturedVideo.size > 30_000_000) {
      this.controllerService.presentToast({
        message: 'Ukuran video tidak boleh lebih dari 30MB',
        color: 'danger',
        duration: 3000,
      });
      return;
    }

    const { base64Data } = await this.convertVideoUrlToBase64(capturedVideo.fullPath);
    this.updateVideoData({
      base64Data,
      capturedVideo,
      videoPath: capturedVideo.fullPath,
    });
  }

  async onTakeVideoFromFile() {
    const result = await FilePicker.pickFiles({ limit: 1, readData: true, types: ['video/mp4', 'video/*'] });
    const file = result.files[0];

    console.log({ file });

    if (file.size > 30_000_000) {
      this.controllerService.presentToast({
        message: 'Ukuran video tidak boleh lebih dari 30MB',
        color: 'danger',
        duration: 3000,
      });
      return;
    }

    this.updateVideoData({
      base64Data: file.data,
      capturedVideo: file,
      videoPath: file.path,
      videoTakenWith: 'file_picker',
    });
  }

  async convertVideoUrlToBase64(url: string) {
    const file = await Filesystem.readFile({ path: url });

    console.log(file.data);

    let base64Data = '';

    if (typeof file.data === 'string') {
      base64Data = window.btoa(file.data);
    } else {
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result === 'string') {
          base64Data = window.btoa(reader.result);
        } else {
          const arrayBuffer = reader.result;
          const byteArray = new Uint8Array(arrayBuffer);
          const stringData = String.fromCharCode.apply(null, byteArray);
          base64Data = window.btoa(stringData);
        }
      };

      reader.readAsArrayBuffer(file.data);
    }

    // Use base64Data in your Angular component
    const videoSrc = 'data:video/mp4;base64,' + base64Data;
    return { videoSrc, base64Data };
  }

  async playRecording() {
    if (this.videoTakenWith === 'file_picker') {
      await FileOpener.openFile({
        path: decodeURIComponent(this.videoPath),
      });
      return;
    }

    await CapacitorVideoPlayer.initPlayer({
      mode: 'fullscreen',
      url: this.videoPath,
      playerId: 'fullscreen',
      componentTag: 'app-product-rating',
      pipEnabled: false,
    });
  }

  onRemoveImage(id: string) {
    this.uploadedImages = this.uploadedImages.filter(image => image.id !== id);
    this.changeRatingHandler();
  }

  updateVideoData({
    capturedVideo,
    base64Data,
    videoPath,
    videoTakenWith,
  }: {
    videoPath: string;
    base64Data: string;
    videoTakenWith?: VideoTakenWith;
    capturedVideo?: MediaFile | File | PickedFile;
  }) {
    this.video = capturedVideo;
    this.videoPath = videoPath;
    this.videoBase64 = base64Data;
    this.videoTakenWith = videoTakenWith;
    this.changeRatingHandler();
  }

  onRemoveVideo() {
    this.video = null;
    this.videoBase64 = null;
    this.videoPath = null;
    this.videoTakenWith = null;
    this.changeRatingHandler();
  }

  async openFullImage(image: string) {
    const isMobile = window.innerWidth <= 576;

    const mobileMmodal = await this.modalController.create({
      component: ModalOpenFullimageComponent,
      cssClass: 'modal-bottom',
      componentProps: {
        imgAlert: image,
        useCloseBtn: false,
      },
    });

    const desktopModal = await this.modalController.create({
      component: ModalCustomAlertComponent,
      cssClass: 'modal-bottom modal-fullscreen',
      componentProps: {
        imgAlert: image,
        useCloseBtn: true,
      },
    });

    if (isMobile) {
      mobileMmodal.present();

      const { data, role } = await mobileMmodal.onDidDismiss();

      if (role === 'cancel') {
        return;
      }
    } else {
      desktopModal.present();

      const { data, role } = await desktopModal.onDidDismiss();

      if (role === 'cancel') {
        return;
      }
    }
  }

  async onChooseVideo() {
    const platform = Capacitor.getPlatform();
    const isNative = platform === 'android' || platform === 'ios';

    if (!isNative) {
      this.controllerService.presentToast({
        message: 'Saat ini rekam video tidak tersedia pada platform web',
        color: 'warning',
        duration: 3000,
      });
      return;
    }

    const actionSheet = await this.actionSheetController.create({
      header: 'Video',
      buttons: [
        {
          text: 'Ambil dari File',
          handler: async () => {
            await actionSheet.dismiss();
            await this.onTakeVideoFromFile();
          },
        },
        {
          text: 'Rekam Video baru',
          handler: async () => {
            await actionSheet.dismiss();
            await this.onRecordVideo();
          },
        },
        // {
        //   role: 'cancel',
        //   text: 'Batal',
        // },
      ],
    });

    await actionSheet.present();
  }

  isRated(index: number): boolean {
    return this.selectedRating !== null && index < this.selectedRating;
  }

  rateProduct(index: number): void {
    if (index + 1 === this.selectedRating) {
      this.selectedRating = null;
    } else {
      this.selectedRating = index + 1;
    }

    this.changeRatingHandler();
  }

  onChangeTextarea(event: Event) {
    const customEvent = event as CustomEvent<IonTextarea>;
    this.review_note = customEvent.detail.value;

    this.changeRatingHandler();
  }

  getTextRating(index: number): string {
    let text = '';

    switch (index) {
      case 1:
        text = 'Sangat Buruk';
        break;
      case 2:
        text = 'Buruk';
        break;
      case 3:
        text = 'Cukup';
        break;
      case 4:
        text = 'Baik';
        break;
      case 5:
        text = 'Sangat Baik';
        break;
      default:
        text = '';
        break;
    }

    return text;
  }
}
